import { groceryEventSchema, groceryEventTypes, type GroceryEvent } from '@fewerbags/schema';

/**
 * The slice of `EventSource` the live-sync wiring needs. The server sends
 * every grocery event as a *named* SSE frame (`event: item.checked`), and
 * named frames are only delivered to listeners registered for that exact
 * name — never to `onmessage` — so we subscribe to each type explicitly.
 */
export interface GroceryEventTarget {
  addEventListener(type: string, listener: (event: { data?: unknown }) => void): void;
  removeEventListener(type: string, listener: (event: { data?: unknown }) => void): void;
}

/** Parse one frame's `data`; null for anything that isn't a valid grocery event. */
export function parseGroceryEventData(data: unknown): GroceryEvent | null {
  if (typeof data !== 'string' || data === '') return null;
  let raw: unknown;
  try {
    raw = JSON.parse(data);
  } catch {
    return null;
  }
  const parsed = groceryEventSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Subscribe `onEvent` to every grocery event name (from the schema, so a new
 * event type is picked up automatically). Returns the unsubscribe function.
 */
export function bindGroceryEvents(
  target: GroceryEventTarget,
  onEvent: (event: GroceryEvent) => void,
): () => void {
  const listener = (frame: { data?: unknown }) => {
    const event = parseGroceryEventData(frame.data);
    if (event) onEvent(event);
  };
  for (const type of groceryEventTypes) target.addEventListener(type, listener);
  return () => {
    for (const type of groceryEventTypes) target.removeEventListener(type, listener);
  };
}
